import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { CompassLogo } from "../components/CompassLogo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { KeyRound, CheckCircle2 } from "lucide-react";

/**
 * Mirrors `PASSWORD_MIN_LENGTH` in `api/src/auth/passwordPolicy.ts` — the
 * same client-side mirror `ForcePasswordChange.jsx` keeps, for the same
 * reason: this page used to say 6 while the API's shared policy (which
 * `/auth/reset-password` now enforces, issue #410) says 10, so every
 * 6-to-9-character attempt cleared the client check and then 400'd.
 */
const PASSWORD_MIN_LENGTH = 10;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== confirm) return toast.error("Passwords don't match");
    if (pw.length < PASSWORD_MIN_LENGTH) return toast.error(`Password must be at least ${PASSWORD_MIN_LENGTH} characters`);
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => nav("/login"), 1500);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reset failed");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-lg px-3 py-2 inline-flex mb-6">
          <CompassLogo size={44} />
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: done ? "#22c55e15" : "#0B2E5915" }}>
            {done ? <CheckCircle2 size={22} color="#22c55e" /> : <KeyRound size={22} color="#0B2E59" />}
          </div>
          <h2 className="font-display font-black text-2xl text-slate-900">
            {done ? "Password updated" : "Choose a new password"}
          </h2>

          {!token && !done && (
            <p className="text-sm text-red-600 mt-3">This link is missing a reset token. <Link to="/forgot-password" className="underline">Request a new one</Link>.</p>
          )}

          {token && !done && (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label>New password</Label>
                <Input data-testid="rp-password" type="password" required minLength={PASSWORD_MIN_LENGTH} value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1.5 h-11" />
              </div>
              <div>
                <Label>Confirm password</Label>
                <Input data-testid="rp-confirm" type="password" required minLength={PASSWORD_MIN_LENGTH} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5 h-11" />
              </div>
              <Button data-testid="rp-submit" type="submit" disabled={busy} className="w-full h-11" style={{ background: "#0B2E59" }}>
                {busy ? "Updating…" : "Update password"}
              </Button>
            </form>
          )}

          {done && (
            <p className="text-slate-600 mt-3 text-sm">Redirecting you to sign in…</p>
          )}
        </div>
      </div>
    </div>
  );
}
